/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {GenericObject, SensorUnits} from '@ion/core/src/types/entities';
import {isImperial} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {genericUnitConversion} from '@ion/core/src/utils/Conversions';
import {toFixed} from '@ion/core/src/utils/NumberUtil';
import {dlsColor} from '../../theme/theme';
import {useThemeMode} from '../../theme/useThemeContext';

export interface TemperatureCellProps {
  value: string;
  uom: Unit;
  breached?: boolean;
  includeIcon?: boolean;
  style?: GenericObject;
  maxTemperatureThreshold?: string | number;
  minTemperatureThreshold?: string | number;
  showDiffs?: boolean;
  inputUnit?: string;
};

const useStyles = makeStyles(() => ({
  container : {
    display    : 'flex',
    alignItems : 'center'
  },
  icon : {
    margin : '8px 4px 0 0'
  }
}));

/**
 * Definig the TemperatureCell that is responsible for displaying the temperature
 * value with the degree and unit of measurement.
 * 
 * ```typescript
 * 
 *  import {TemperatureCell} from '@ion/dls/src/components/grid';
 * 
 *  <TemperatureCell value={data.temperature} uom={getUserUnit()} />
 * 
 * ```
 */
export const TemperatureCell = ({
  value,
  uom,
  breached,
  inputUnit,
  maxTemperatureThreshold,
  minTemperatureThreshold,
  includeIcon = false,
  showDiffs = false,
  style = {}
}: TemperatureCellProps) => {
  const classes      = useStyles();
  const {mode}       = useThemeMode();
  const unit         = SensorUnits[isImperial(uom) ? 'F' : 'C'];
  const tempBreached = isTrue(breached);

  const ThresholdDiff = useMemo(() => {
    if (showDiffs && tempBreached) {
      const max = genericUnitConversion(maxTemperatureThreshold, inputUnit, {value: uom}, 2);
      const min = genericUnitConversion(minTemperatureThreshold, inputUnit, {value: uom}, 2);
      const difference = Number(max) < Number(value) ? Number(value) - max : Number(value) - min;
      const val = difference > 0 ? `+${toFixed(difference, 2)}` : `${toFixed(difference, 2)}`;
      
      return (
        <span style={{
          ...style,
          color : tempBreached ? dlsColor('caRed') : undefined
        }}>
          {` (${val}`}<sup>o</sup>{unit}{')'}
        </span>
      );
    }
  }, [
    value,
    showDiffs,
    breached,
    inputUnit,
    maxTemperatureThreshold,
    minTemperatureThreshold,
    uom,
    style,
    mode
  ]);

  if (value) {
    return (
      <div className={classes.container}>
        {includeIcon && tempBreached && (
          <span className={classes.icon}>
            <FiberManualRecordIcon color='error' fontSize='inherit' />
          </span>
        )}
        <span style={{
          ...style,
          color : tempBreached ? dlsColor('caRed') : undefined
        }}>
          {value}<sup>o</sup>{unit}
        </span>
        {ThresholdDiff}
      </div>
    );
  }
};

export default TemperatureCell;
